<template>
  <div>
    <v-dialog
      v-model="dialog"
      :fullscreen="$vuetify.display.smAndDown"
      :transition="
        $vuetify.display.smAndDown
          ? 'dialog-bottom-transition'
          : 'dialog-transition'
      "
      max-width="768"
    >
      <template #activator="{ props: activatorProps }">
        <v-btn
          color="primary"
          width="125"
          rounded
          :disabled="disabled"
          v-bind="activatorProps"
          >Reschedule</v-btn
        >
      </template>
      <v-card>
        <v-toolbar dark color="secondary">
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title
            >Reschedule Appointment #{{ appointment.job_id }}</v-toolbar-title
          >
        </v-toolbar>
        <div class="py-6">
          <v-form
            ref="form"
            v-model="formIsValid"
            lazy-validation
            @submit.prevent="handleSubmit"
          >
            <v-card
              class="d-flex flex-column align-center justify-center text-grey-darken-2 pa-2 bordered-card text-center mx-4 mb-4"
            >
              <p class="title ma-0">{{ appointment.service_category.name }}</p>
              <p class="caption ma-0">
                {{ address.street }},
                {{ address.zip_code }}
              </p>
              <p class="caption mt-2 mb-0">Currently scheduled for</p>
              <p class="ma-0 text-secondary">
                <b>{{ appointmentDate }}</b>
              </p>
              <p class="ma-0 text-secondary">
                <b>{{ appointmentWindow }}</b>
              </p>
            </v-card>
            <TimeSlots
              ref="timeSlots"
              :service-category="appointment.service_category"
              :address="address"
              :handle-select-slot="handleSelectSlot"
              :error-message="timeSlotsError"
            />
            <div class="text-center">
              <v-btn
                type="submit"
                color="primary"
                class="mt-6"
                rounded
                :loading="loading"
                :disabled="!selectedSlot"
                >Reschedule Appointment</v-btn
              >
            </div>
          </v-form>
        </div>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar" color="success" top multi-line>
      <v-icon class="text-white mr-2">mdi-check</v-icon>
      <div class="d-flex flex-column full-width">
        <span>Rescheduled for</span>
        <span>{{ appointmentDate }} {{ appointmentWindow }}</span>
      </div>
      <v-btn color="white" icon variant="text" @click="snackbar = false">
        <v-icon>mdi-close-circle</v-icon>
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { getAppointmentWindow, prettyFormat } from "@/lib/date";

import TimeSlots from "@/components/TimeSlots.vue";
import API from "@/api-client";
import ValidationMixin from "@/mixins/validationRules";

export default {
  components: {
    TimeSlots
  },
  mixins: [ValidationMixin],
  props: {
    appointment: {
      type: Object,
      default: () => ({})
    },
    address: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false,
      loading: false,
      selectedSlot: null,
      snackbar: false,
      timeSlotsError: ""
    };
  },
  computed: {
    appointmentDate() {
      return prettyFormat(new Date(this.appointment.scheduled_time));
    },
    appointmentWindow() {
      const timeZone = this.address.time_zone;
      return getAppointmentWindow(this.appointment, timeZone);
    }
  },
  methods: {
    ...mapActions(["fetchJob", "updateAppointment"]),
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (valid) {
        this.loading = true;
        this.timeSlotsError = "";
        try {
          await this.updateAppointment({
            id: this.appointment.id,
            data: {
              scheduled_time: this.selectedSlot.scheduled_time,
              arrival_window_start: this.selectedSlot.arrival_window_start,
              arrival_window_end: this.selectedSlot.arrival_window_end
            }
          });
          this.fetchJob(this.appointment.job_id);
          this.dialog = false;
          this.snackbar = true;
          window.gtag("event", "reschedule", {
            event_category: "appointment",
            appointment_id: this.appointment.id
          });
        } catch (e) {
          const errorMessage = e.response?.data?.message || e.message || "An error occurred";

          if (errorMessage.includes("Please choose a different time.")) {
            this.selectedSlot = null;
            this.timeSlotsError = "The selected time slot is no longer available. Please choose a different time.";

            if (this.$refs.timeSlots && this.$refs.timeSlots.resetDatesCache) {
              this.$refs.timeSlots.resetDatesCache();
              this.$refs.timeSlots.getAutoSuggestedSlot();
            }
          } else {
            throw new Error(e);
          }
        }
        this.loading = false;
      }
    },
    handleSelectSlot(slot) {
      this.selectedSlot = slot;
      this.timeSlotsError = "";
    }
  }
};
</script>

<style lang="scss" scoped></style>
